'use client';
import Link from 'next/link';
import { useResource } from '@/lib/use-resource';

interface IntegrityCheck { id: string; label: string; ok: boolean; detail: string; severity: 'critical' | 'warning' }
interface IntegrityResult { asOf: string; anyFailed: boolean; anyCritical: boolean; checks: IntegrityCheck[] }

// In-page data-integrity strip. Lives ON the pipeline page (the section
// dedicated to finding problems) — NOT pinned to the top of the whole app.
// Renders nothing when no critical check is failing. Critical-only by design:
// the silent-lying class (stale scoring inputs, missing prices, dark scores
// file). Warning-tier items live in the pipeline tables.
export function DataIntegrityBanner() {
  const { data } = useResource<IntegrityResult>('/api/data-integrity', 60_000);
  const critical = data?.checks.filter((c) => !c.ok && c.severity === 'critical') ?? [];
  if (critical.length === 0) return null;
  return (
    <div
      role="alert"
      className="mb-3 rounded-md bg-red-500/10 border border-red-500/40 px-4 py-2 text-[11px] text-red-200 flex items-center gap-3 flex-wrap"
      title={critical.map((c) => `${c.label}: ${c.detail}`).join('\n')}
    >
      <span className="w-1.5 h-1.5 rounded-full bg-red-400 shadow-[0_0_6px_rgba(248,113,113,0.9)] animate-pulse flex-shrink-0" aria-hidden />
      <span className="font-semibold uppercase tracking-wide">Data integrity</span>
      <span className="text-red-300/80">
        {critical.length} critical check{critical.length === 1 ? '' : 's'} failing:
      </span>
      <span className="text-red-100/90 font-mono">{critical.map((c) => c.label).join(' · ')}</span>
      <Link
        href="/pipeline?tab=protection"
        className="ml-auto px-2 py-0.5 rounded border border-red-400/40 hover:bg-red-400/15 text-[10px] uppercase tracking-wide font-semibold"
      >
        details
      </Link>
    </div>
  );
}
